/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { LibrarySearch } from './LibrarySearch';
import LocalFileSearch from './LocalFileSearch';
import UssSearch from './UssSearch';
import MockSearch from './MockSearch';
import NullSearch from './NullSearch';
declare type SearchableTypes = 'USS' | 'Lightroom' | 'LocalFiles' | 'Library' | 'Mock';

class SearchFactory {
	static instance: SearchFactory | null;

	constructor() {
		if (SearchFactory.instance) {
			return SearchFactory.instance;
		}

		SearchFactory.instance = this;
	}

	createSearchable(hostContext: HostContext, type: SearchableTypes, params: Record<string, any>): Searchable {
		switch (type) {
			case 'USS':
				return new UssSearch(params);

			case 'LocalFiles':
				return new LocalFileSearch(params);

			case 'Library':
				return new LibrarySearch(hostContext, params);

			case 'Mock':
				return new MockSearch(params);

			default:
				return new UssSearch(params);
		}
	}

	createSearchableSet(
		hostContext: HostContext,
		filter: SelectedUiFilters,
		searchParams: Record<string, any>,
		searchControl: Record<string, any>,
	): Array<Searchable> {
		const searchables: Array<Searchable> = [];

		const useUSSSearch = this.useUSSSearchFromSearchControl(searchControl);
		if (useUSSSearch && navigator.onLine) {
			const ussBody = Object.assign({}, searchParams);
			const param = {
				searchParams: ussBody,
				endpointPath: '/v2/users/me/search-assets',
				hostContext,
			};
			searchables.push(this.createSearchable(hostContext, 'USS', param));
		}

		if (searchables.length === 0) {
			// return a searchable that returns no results
			searchables.push(new NullSearch());
		}

		return searchables;
	}

	private useUSSSearchFromSearchControl(searchControl: Record<string, any>) {
		let useUSSSearch = true;

		// if searchControl is _not_ an empty object
		if (searchControl && Object.keys(searchControl).length > 0) {
			// If the 'ussSearch' variable is in the searchControl, set the enable value here :thinking: TODO: REVISE THIS
			if (typeof searchControl.ussSearch === 'boolean') {
				useUSSSearch = searchControl.ussSearch;
			}
		}

		return useUSSSearch;
	}
}

export default new SearchFactory();
